import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import { toastModule } from "@/modules/toast";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { getFileExtensionFromMimeType } from "@/modules/audio-recording/upload/mime-utils";
import { audioRecordingModule } from "@/modules/audio-recording";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export const downloadRecordingAudio = async ({
  recording,
}: {
  recording: AudioRecordingObservable | null;
}) => {
  if (!recording) return;

  const blobs = await recording.store.dexie.repositories.localAudioRecordingBlobs.table
    .where({ associatedAudioRecordingId: recording.id })
    .sortBy("blobIndex");
  const blob = await audioRecordingModule.transformBlobToPlayableFormat({
    blobs: blobs.map(record => record.rawBlob),
  });
  await saveBlob(blob);
};

export const downloadArtifactAudio = async ({
  artifactInfo,
}: {
  artifactInfo: NoteSourceArtifactInfo;
}) => {
  if (artifactInfo?.kind !== "AUDIO" || !artifactInfo.data) return;

  const url = artifactInfo.data.download_url;
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download audio status: ${response.status}`);
    }
    const audioBlob = await response.blob();
    await saveBlob(audioBlob, artifactInfo.data.mime_type);
  } catch (error) {
    logger.error({
      message: "Failed to download audio",
      info: {
        url,
        error: objectModule.safeErrorAsJson(error as Error),
      },
    });
    toastModule.triggerToast({ content: "Failed to download audio" });
  }
};

const saveBlob = async (blob: Blob | null, mimeTypeStr?: string) => {
  if (!blob || !blob.size) {
    toastModule.triggerToast({ content: "Recording is empty" });
    return;
  }
  const audioUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = audioUrl;
  const extension = getFileExtensionFromMimeType({ mimeTypeStr: mimeTypeStr ?? blob.type });
  a.download = `Mem Audio Recording.${extension}`;
  a.click();

  // Calculate timeout based on file size: ~10ms per MB, minimum 500ms, maximum 5s
  // For a 200 MB file, this gives ~2s which should be sufficient
  const timeoutMs = Math.min(5_000, Math.max(500, (blob.size / 1_000_000) * 10));
  setTimeout(() => {
    // Clean up the object URL after download starts or fails
    URL.revokeObjectURL(audioUrl);
  }, timeoutMs);
  toastModule.triggerToast({ content: "Recording saved" });
};
